import React, {useContext, useState, useEffect, useRef, useMemo} from "react";
import {useReactToPrint} from "react-to-print";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faPrint, faFileExport, faBoxArchive, faFilter} from "@fortawesome/free-solid-svg-icons";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {FacilityNavContext} from "../../contexts/facilitynav.js";
import {useToast} from "../../contexts/toast.js";
import {useSocket} from "../../contexts/socket.js";

// Hooks
import useApi from "../../hooks/useApi.js";

// Utils
import {startExport} from "../../utils/export.js";
import {formatSubmissions, getAncestors, mergeBuilders} from "../../utils/builder.js";
import {isMobileSize} from "../../utils/responsive.js";

import {prettyDateInUserTimezone, prettyDateWithDayInUserTimezone} from "../../utils/helpers.js";
import {getWeatherSourceLinks} from "../../utils/weather-links.js";
import {
  filterReviewsForGate,
  toWeatherContext,
  withRecordConditionWeather
} from "../tasks/reviewConditions.js";

// Components
import RenderSubmission from "./RenderSubmission.js";
import RenderChecksheet from "../checksheet-builder/RenderChecksheet.js";
import InputText from "../../components/form/InputText.js";
import InputCheck from "../../components/form/InputCheck.js";
import InputCheckGroup from "../../components/form/InputCheckGroup.js";
import Weather from "../../components/Weather.js";
import RecordResponsibility from "./RecordResponsibility.js";
import RecordHistory from "./RecordHistory.js";
import RecordNotes from "./RecordNotes.js";
import Badge from "../../components/Badge.js";
// Modals
import Modal from "../../components/Modal.js";
import ModalFilter from "../checksheet-builder/ModalFilter.js";
import ModalReassignUser from "./ModalReassignUser.js";
import ModalRecordVersionNote from "./ModalRecordVersionNote.js";
import ModalReview from "../tasks/ModalReview.js";

// Style
import {border, colors, pad, radius, shadow} from "../../style/components/variables.js";
import {bp, breakpoint} from "../../style/components/breakpoints.js";
import {flex, z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {
  Abbr,
  Button,
  ButtonLoader,
  Form,
  FormField,
  FormGroup,
  Heading,
  Inline,
  Loader,
  NotLoaded,
  Pill,
  Title
} from "../../style/components/general.js";

// Socket Constants
import {getFacilityRooms, LOCK, RELEASE} from "../general/Room.js";
import {NavContext} from "../../contexts/nav.js";

const EXPORT_REQUIREMENTS = [
  "Top level must contain groups only (no standalone fields or components).",
  "Every group must have the same fields, matching label and type.",
  "Components inside a group are not allowed."
];

const formatExportReason = reason => {
  if (!reason) return null;

  const reasonMap = {
    "This checksheet contains fields on the top level of the checksheet.":
      "A field exists outside of a group.",
    "This checksheet contains components.":
      "A component exists at the top level (not inside a group).",
    "Field lengths do not match on all groups.": "Groups have different numbers of fields.",
    "Field labels do not match on all fields.": "Groups have different field labels or types."
  };

  return reasonMap[reason] || reason;
};

const ExportUnavailableInfo = ({reason}) => (
  <>
    <ExportInfoTitle>Export not available for this checksheet</ExportInfoTitle>
    <ExportInfoList>
      {EXPORT_REQUIREMENTS.map(item => (
        <li key={item}>{item}</li>
      ))}
    </ExportInfoList>
    {reason && (
      <>
        <ExportInfoDetailLabel>Details</ExportInfoDetailLabel>
        <ExportInfoDetail>{formatExportReason(reason)}</ExportInfoDetail>
      </>
    )}
  </>
);

ExportUnavailableInfo.propTypes = {
  reason: PropTypes.string
};

dayjs.extend(utc);
dayjs.extend(timezone);

const ModalRecordView = ({
  visible,
  setVisible,
  record,
  checksheet,
  locks,
  updateData,
  editing = false,
  hasBackButton = false,
  goBack
}) => {
  const socket = useSocket();

  const {addToast} = useToast();

  const {slug} = useMemo(() => checksheet?.facility || {}, [checksheet]);

  const [state, setState] = useState(undefined);
  const [selectedRecord, setSelectedRecord] = useState(record);
  const [detailLoading, setDetailLoading] = useState(true);

  const builder = useMemo(
    () =>
      selectedRecord?.state?.id
        ? state?.builder || checksheet.builder
        : checksheet.builder,
    [state, checksheet, selectedRecord]
  );

  const {allIds, byId} = useMemo(
    () =>
      state !== undefined
        ? mergeBuilders(builder, checksheet.facility.builder)
        : {allIds: [], byId: {}},
    [checksheet, builder, state]
  );

  const hasNotes = useRef(false);
  const headerRef = useRef();
  const weatherRef = useRef();
  const recordRef = useRef();
  const notesRef = useRef();
  const printRef = useRef();

  const configurePrint = () => {
    const printBody = document.createElement("div");
    printBody.style.pageBreakInside = "avoid";

    if (weatherRef?.current) {
      const weatherClone = weatherRef.current.cloneNode(true);
      printBody.appendChild(weatherClone);
    }

    const headerClone = headerRef.current.cloneNode(true);
    printBody.appendChild(headerClone);

    if (hasNotes.current) {
      const recordNotesWrapper = document.createElement("div");
      recordNotesWrapper.style.display = "flex";
      recordNotesWrapper.style.justifyContent = "start";
      recordNotesWrapper.style.flexWrap = "nowrap";
      recordNotesWrapper.style.gap = "10px";

      const notesClone = notesRef.current.cloneNode(true);
      const recordClone = recordRef.current.cloneNode(true);

      const recordWrapper = document.createElement("div");
      recordWrapper.style.width = "65%";

      recordWrapper.appendChild(recordClone);

      const notesWrapper = document.createElement("div");
      notesWrapper.style.width = "34%";
      notesWrapper.style.marginTop = "55px";

      notesWrapper.appendChild(notesClone);

      recordNotesWrapper.appendChild(recordWrapper);
      recordNotesWrapper.appendChild(notesWrapper);

      printBody.appendChild(recordNotesWrapper);
    } else {
      const recordClone = recordRef.current.cloneNode(true);
      printBody.appendChild(recordClone);
    }

    printRef.current = printBody;
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef
  });

  const {allStates} = useContext(NavContext);
  const {currentUser, atLeast, roleCanAccessResource} = useContext(AuthContext);
  const {facility} = useContext(FacilityNavContext);
  const {settings} = useContext(SettingsContext);

  const weatherSourceLinks = useMemo(
    () =>
      getWeatherSourceLinks({
        ...facility?.primaryAddress,
        weatherSource: facility?.weatherSource
      }),
    [facility?.primaryAddress, facility?.weatherSource]
  );

  const [versions, setVersions] = useState();
  const [currentVersion, setCurrentVersion] = useState(null);
  const [edit, setEdit] = useState(editing);
  const [editUser, setEditUser] = useState(editing);
  const [exporting, setExporting] = useState(false);
  const [refreshNotes, setRefreshNotes] = useState(false);
  const [viewMode, setViewMode] = useState("record");
  const [viewModification, setViewModification] = useState(false);
  const [fieldTags, setFieldTags] = useState([]);
  const [updatingVersions, setUpdatingVersions] = useState(false);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [queuedEdit, setQueuedEdit] = useState(false);
  const [showModalReview, setShowModalReview] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  // Filters
  const [filters, setFilters] = useState(null);
  const [filterResult, setFilterResult] = useState(null);
  const [showModalFilter, setShowModalFilter] = useState(false);
  // Export
  const [loadingExport, setLoadingExport] = useState(false);
  const [exportStatus, setExportStatus] = useState("loading");
  const [exportReason, setExportReason] = useState(null);

  const {api: apiUser} = useApi("users");
  const {api: apiState} = useApi("checksheet-states");
  const {api: apiChecksheetRecords} = useApi("checksheet-records");
  const {api: apiRecords} = useApi("checksheet-records", {suppress: {success: true, error: true}});
  const {api: apiVersions} = useApi("checksheet-record-versions");
  const {api: apiFieldTags} = useApi("field-tags");

  const {submission} = selectedRecord || {};

  // List rows are slim; always hydrate detail (+ previous) when the modal opens.
  useEffect(() => {
    if (!visible || !record?.id) return undefined;

    let cancelled = false;
    setDetailLoading(true);

    apiChecksheetRecords.callGet(record.id, {withPrevious: true}).then(({status, data}) => {
      if (cancelled) return;
      if (status === 200 && data) {
        setSelectedRecord(data);
        hasNotes.current =
          Object.keys(data.postedNotes || {}).length > 0 ||
          Object.keys(data.archivedNotes || {}).length > 0 ||
          Object.keys(data.resolvedNotes || {}).length > 0;
      }
      setDetailLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, record?.id, apiChecksheetRecords]);

  useEffect(() => {
    if (selectedRecord?.state?.id) {
      apiState.callGet(selectedRecord.state.id).then(({status, data}) => {
        if (status === 200 && data) setState(data);
      });
    } else {
      setState(null);
    }
  }, [selectedRecord?.state?.id, apiState]);

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return [
      ...Object.entries(allStates).map(([name, {id, color}]) => ({
        name: name.toUpperCase(),
        id: `${id}`,
        color
      }))
    ];
  }, [allStates]);

  const facilityState = useMemo(() => {
    if (!facilityStates || !state) return null;
    return facilityStates.find(({id}) => id === `${state.facilityStateId}`) || null;
  }, [state, facilityStates]);

  const options = useMemo(
    () =>
      Object.keys(selectedRecord?.submission?.responses || {})
        .filter(key => key in byId)
        .map(key => ({
          name: `${getAncestors(byId[key].parentName, byId)} ${byId[key].label}`,
          value: key
        })),
    [byId, selectedRecord?.submission?.responses]
  );

  const schema = yup.object().shape({
    exportName: yup.string().required("Name is required."),
    help: yup.boolean(),
    fields: yup
      .array()
      .nullable()
      .test({
        test: val => Array.isArray(val) && val?.length > 0,
        message: "One or more fields must be selected for export."
      })
  });

  const initialValues = {
    exportName: "",
    help: false,
    fields: []
  };

  const form = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(schema)
  });
  const {handleSubmit, reset} = form;

  const filterCount = useMemo(() => {
    let count = 0;
    if (filters?.types?.length) count += filters.types.length;
    if (filters?.tags?.length) count += filters.tags.length;
    return count;
  }, [filters]);

  // Initial Load
  useEffect(() => {
    if (!selectedRecord?.id || detailLoading) return;

    apiVersions.callGet(null, {recordId: selectedRecord.id}).then(({status, data}) => {
      if (status === 200 && data?.versions) setVersions(data.versions);
      setUpdatingVersions(false);
    });

    apiFieldTags.callGet().then(({status, data}) => {
      if (status === 200 && data)
        setFieldTags(data.map(({id, name}) => ({id, name, label: name.toUpperCase()})));
    });
  }, [socket, currentUser, apiVersions, apiFieldTags, selectedRecord?.id, detailLoading]);

  const handleEdit = (values, outOfRange = null, outOfRangeMultiple = null, draft = false) => {
    if (draft) setDraftLoading(true);
    else setCompleteLoading(true);
    const {completedAt} = values;
    const responses = {...values};
    delete responses.completedAt;

    const formatted = formatSubmissions(responses, builder);

    const edited = {
      submission: {
        name: submission.name,
        frequency: submission.frequency,
        responses: formatted
      },
      userId: currentUser.publicId,
      draft,
      completedAt: completedAt || null,
      outOfRange: outOfRange ? Object.keys(outOfRange) : null,
      outOfRangeMultiple: outOfRangeMultiple ? Object.keys(outOfRangeMultiple) : null
    };

    if (selectedRecord.draft || selectedRecord.overdue)
      apiChecksheetRecords
        .callPut(selectedRecord.id, edited)
        .then(({status}) => {
          if (status === 200) {
            socket.emit(
              RELEASE,
              getFacilityRooms(slug, "checksheet"),
              `checksheet_${checksheet.id}_record_${record.id}`
            );
            updateData();
            setVisible(false);
          }
        })
        .finally(() => {
          setCompleteLoading(false);
          setDraftLoading(false);
        });
    else {
      setQueuedEdit(edited);
      setShowExplanationModal(true);
    }
  };

  const handleNote = note =>
    apiChecksheetRecords.callPut(selectedRecord.id, {...queuedEdit, note}).then(({status}) => {
      if (status === 200) {
        socket.emit(
          RELEASE,
          getFacilityRooms(slug, "checksheet"),
          `checksheet_${checksheet.id}_record_${record.id}`
        );
        updateData();
        setVisible(false);
      }
    });

  const handleUpdateUser = ({user}) => {
    if (user) {
      const edited = {
        completedBy: user.publicId
      };
      if (selectedRecord.draft)
        apiChecksheetRecords.callPut(selectedRecord.id, edited).then(({status, data}) => {
          if (status === 200) {
            setUpdatingVersions(true);
            updateData();
            // The PUT response omits conditionWeather; keep the detail fetch's value.
            setSelectedRecord(prev => ({conditionWeather: prev?.conditionWeather, ...data.data}));
            setEditUser(false);
          }
        });
      else {
        setQueuedEdit(edited);
        setShowExplanationModal(true);
      }
    }
  };

  useEffect(() => {
    if (!visible) setEdit(false);
  }, [setVisible, visible]);

  useEffect(() => {
    if (refreshNotes) {
      setRefreshNotes(false);
      updateData();
    }
  }, [refreshNotes, updateData]);

  // Handle filtering
  useEffect(() => {
    if (filters?.types || filters?.tags) {
      setFilterResult(
        Object.fromEntries(
          Object.entries(byId).filter(
            ([, {tag, type}]) =>
              filters.types?.includes(type) || (tag && filters.tags?.includes(tag))
          )
        )
      );
    } else {
      setFilters(null);
      setFilterResult(null);
    }
  }, [filters, allIds, byId]);

  // Check if export is available
  useEffect(() => {
    if (!selectedRecord?.id || detailLoading || !submission) return;

    setExportStatus("loading");
    setExportReason(null);

    apiRecords
      .callPatch(null, {
        facilityChecksheetRecordId: selectedRecord.id,
        exportName: "test",
        help: false
      })
      .then(res => {
        if (typeof res === "string" || !res?.status) {
          setExportStatus("blocked");
          setExportReason("Unable to verify export availability.");
          return;
        }

        if (res.status === 200) {
          if (res.data?.exportAvailable === false) {
            setExportStatus("blocked");
            setExportReason(res.data.exportReason || null);
          } else {
            setExportStatus("allowed");
            setExportReason(null);
          }
          return;
        }

        setExportStatus("blocked");
        setExportReason("Unable to verify export availability.");
      });
  }, [apiRecords, selectedRecord?.id, detailLoading, submission]);

  const createExport = async values => {
    setLoadingExport(true);
    const {exportName, fields, help} = values;
    const params = {
      facilityChecksheetRecordId: selectedRecord.id,
      exportName,
      help: help || false
    };

    if (fields?.length > 0) params["fields"] = fields.toString();

    apiRecords
      .callPatch(null, params)
      .then(res => {
        const {status, data} = res;
        if (status === 200 && data) {
          const rows = [];
          const {body, header} = data;
          body.map(row => {
            const temp = {};
            row.map(pair => {
              temp[pair.key] = pair.value;
            });
            rows.push(temp);
          });

          startExport("csv", exportName, rows, header);
          setVisible(false);
          reset({...initialValues});
        } else addToast(res, "error");
      })
      .finally(() => setLoadingExport(false));
  };

  const checksheetLocked = () => {
    if (locks && locks[`checksheet_${checksheet.id}_record_${record.id}`]) {
      apiUser
        .callGet(locks[`checksheet_${checksheet.id}_record_${record.id}`].user)
        .then(({status, data}) => {
          if (status === 200 && data) {
            const {firstName, lastName} = data;
            addToast(`Locked by ${firstName} ${lastName}`);
          }
        });
      return true;
    }

    return false;
  };

  const recordDraftUser = useMemo(() => {
    const user = record?.updatedBy || record?.user;
    if (user === "GENERATED") return undefined;
    return user;
  }, [record]);

  if (showExplanationModal)
    return (
      <ModalRecordVersionNote
        visible={showExplanationModal}
        setVisible={setShowExplanationModal}
        submitNote={handleNote}
        cancel={() => {
          setQueuedEdit(undefined);
          setShowExplanationModal(false);
        }}
      />
    );

  if (editUser)
    return (
      <ModalReassignUser
        visible={visible && editUser}
        setVisible={setVisible}
        goBack={() => setEditUser(false)}
        facilityId={facility?.id}
        update={handleUpdateUser}
        current={
          selectedRecord.completedBy
            ? selectedRecord.completedBy.publicId
            : selectedRecord.user?.publicId
        }
      />
    );

  if (showModalFilter)
    return (
      <ModalFilter
        visible={showModalFilter}
        setVisible={setVisible}
        filters={filters}
        setFilters={setFilters}
        tags={fieldTags}
        hasBackButton
        goBack={() => setShowModalFilter(false)}
      />
    );

  if (showModalReview)
    return (
      <ModalReview
        visible={showModalReview}
        setVisible={setVisible}
        checksheet={checksheet}
        goBack={() => setShowModalReview(false)}
        confirm={() => {
          setEdit(true);
          socket.emit(
            LOCK,
            getFacilityRooms(slug, "checksheet"),
            `checksheet_${checksheet.id}_record_${record.id}`
          );
          setShowModalReview(false);
        }}
        date={record.dateDue}
      />
    );

  return (
    <Modal
      visible={visible}
      setVisible={s => {
        if (edit && !s)
          socket.emit(
            RELEASE,
            getFacilityRooms(slug, "checksheet"),
            `checksheet_${checksheet.id}_record_${record.id}`
          );
        setVisible(s);
      }}
      isChecksheetPreview
      clickOutsideToClose={false}
      maxWidth={breakpoint.width[5]}
      hasBackButton={hasBackButton}
      goBack={
        goBack
          ? () => {
              if (edit)
                socket.emit(
                  RELEASE,
                  getFacilityRooms(slug, "checksheet"),
                  `checksheet_${checksheet.id}_record_${record.id}`
                );
              goBack();
            }
          : undefined
      }>
      <Row>
        <HeadingWrapper>
          <Heading>Checksheet Submission</Heading>

          <Info ref={headerRef}>
            <Status status={selectedRecord.status} archived={selectedRecord.archived}>
              {selectedRecord.archived && (
                <Abbr title="Archived">
                  <FontAwesomeIcon icon={faBoxArchive} />
                  &nbsp;
                </Abbr>
              )}
              {selectedRecord.status}
            </Status>
            {selectedRecord.conflict && (
              <Conflict>
                <Abbr title="This record has conflicting versions. Please contact system administrator to resolve.">
                  CONFLICT
                </Abbr>
              </Conflict>
            )}
            <span>
              {selectedRecord.dateDue && (
                <MobileBlock overdue={selectedRecord.overdue}>
                  <strong>DATE&nbsp;DUE</strong>:&nbsp;{isMobileSize() && <br />}
                  {prettyDateWithDayInUserTimezone(selectedRecord.dateDue, settings.timezone)}
                </MobileBlock>
              )}
              <br />
              <br />
              {selectedRecord.completedAt && (
                <MobileBlock>
                  <strong>DATE&nbsp;COMPLETED</strong>:&nbsp;{isMobileSize() && <br />}
                  {prettyDateInUserTimezone(
                    selectedRecord.completedAt,
                    settings.timezone,
                    "MMM D, YYYY"
                  )}{" "}
                  <CompletedByWrapper>
                    {selectedRecord.completedBy && (
                      <>
                        by {selectedRecord.completedBy.firstName}&nbsp;
                        {selectedRecord.completedBy.lastName}
                      </>
                    )}
                    {atLeast("super") && (
                      <CompletedByEdit onClick={() => setEditUser(true)} type="button">
                        <FontAwesomeIcon icon={faEdit} />
                      </CompletedByEdit>
                    )}
                  </CompletedByWrapper>
                </MobileBlock>
              )}
            </span>
          </Info>
        </HeadingWrapper>
        <StyledInline>
          {atLeast("admin") &&
            !editing &&
            submission &&
            !selectedRecord.overdue &&
            state !== undefined && (
              <SpacedInline>
                {roleCanAccessResource("facility_checksheet_record_version", "export") &&
                  !edit &&
                  options.length > 0 && (
                    <ExportButtonAnchor>
                      <Export
                        onClick={() => {
                          if (exportStatus === "allowed") setExporting(prev => !prev);
                        }}
                        blocked={exportStatus === "blocked"}
                        loading={exportStatus === "loading"}>
                        {!exporting ? (
                          <abbr title={exportStatus === "blocked" ? undefined : "Export"}>
                            <FontAwesomeIcon icon={faFileExport} />
                          </abbr>
                        ) : (
                          "Cancel"
                        )}
                      </Export>
                      {exportStatus === "blocked" && (
                        <ExportInfoPopover>
                          <ExportUnavailableInfo reason={exportReason} />
                        </ExportInfoPopover>
                      )}
                    </ExportButtonAnchor>
                  )}

                {roleCanAccessResource("facility_checksheet_record_version", "export") &&
                  !edit &&
                  !exporting && (
                    <Export
                      type="button"
                      onClick={() => {
                        configurePrint();
                        handlePrint();
                      }}>
                      <abbr title="Print">
                        <FontAwesomeIcon icon={faPrint} />
                      </abbr>
                    </Export>
                  )}

                {!edit && (
                  <Filters>
                    <FilterButton type="button" onClick={() => setShowModalFilter(true)}>
                      <Badge
                        count={filterCount}
                        title="Active filters"
                        offset="14px"
                        color={colors.heroGreen}
                      />
                      <FontAwesomeIcon icon={faFilter} />
                    </FilterButton>
                  </Filters>
                )}

                {updateData &&
                  roleCanAccessResource("facility_checksheet_record_version", "create") &&
                  !facility?.isDeleted &&
                  !checksheet.isArchived &&
                  !exporting &&
                  !viewModification &&
                  state !== undefined &&
                  (!currentVersion ||
                    versions?.length === 0 ||
                    currentVersion.id === versions[0].id) && (
                    <Edit
                      type="button"
                      locked={
                        locks &&
                        locks[`checksheet_${checksheet.id}_record_${record.id}`] &&
                        locks[`checksheet_${checksheet.id}_record_${record.id}`].user !==
                          currentUser.publicId
                      }
                      onClick={() => {
                        if (!edit && !checksheetLocked()) {
                          setEdit(true);
                          socket.emit(
                            LOCK,
                            getFacilityRooms(slug, "checksheet"),
                            `checksheet_${checksheet.id}_record_${record.id}`
                          );
                        } else if (edit) {
                          setEdit(false);
                          socket.emit(
                            RELEASE,
                            getFacilityRooms(slug, "checksheet"),
                            `checksheet_${checksheet.id}_record_${record.id}`
                          );
                        }
                      }}>
                      {!edit ? (
                        <abbr title="Edit">
                          <FontAwesomeIcon icon={faEdit} />
                        </abbr>
                      ) : (
                        "Cancel"
                      )}
                    </Edit>
                  )}
              </SpacedInline>
            )}
          <SpacedInline>
            <ModeToggle
              onClick={() => {
                setViewMode(prev => (prev === "record" ? "notes" : "record"));
              }}>
              View {viewMode === "record" ? "Notes" : "Record"}
            </ModeToggle>
            {roleCanAccessResource("facility_checksheet_record_version", "create") &&
              selectedRecord.overdue && (
                <Button
                  type="button"
                  onClick={() => {
                    const conditionWeatherTask = withRecordConditionWeather(
                      checksheet,
                      selectedRecord
                    );
                    const weatherContext = toWeatherContext(
                      conditionWeatherTask?.weather,
                      conditionWeatherTask?.precipitationChance
                    );
                    const dateForConditional = dayjs.utc().tz(settings?.timezone || "US/Pacific");
                    const gatedReviews = filterReviewsForGate(checksheet?.reviews, {
                      dateForConditional,
                      weatherContext
                    });
                    if (
                      gatedReviews.length > 0 &&
                      recordDraftUser?.publicId !== currentUser.publicId
                    )
                      setShowModalReview(true);
                    else setEdit(prev => !prev);
                  }}>
                  {edit ? "Cancel" : "Complete"}
                </Button>
              )}
          </SpacedInline>
        </StyledInline>
      </Row>

      <Wrapper>
        <PageLeft data-testid="record.left" mode={viewMode}>
          {state === undefined || detailLoading || !submission ? (
            <NotLoaded>
              <Loader />
            </NotLoaded>
          ) : (
            <>
              {!edit && submission && !exporting && (
                <RenderSubmission
                  ref={recordRef}
                  submission={
                    currentVersion?.submission
                      ? currentVersion.submission
                      : selectedRecord.submission
                  }
                  builder={
                    selectedRecord.overdue && !selectedRecord.draft && !edit
                      ? {allIds: [], byId: {}}
                      : builder
                  }
                  filterResult={filterResult}
                  dateDue={selectedRecord.dateDue}
                  completedAt={selectedRecord.completedAt}
                  startDate={selectedRecord.startDate ?? selectedRecord.dateDue}
                  draft={selectedRecord.draft}
                  modifications={currentVersion?.modification || null}
                  viewModification={viewModification}
                  submissionName={checksheet.name}
                  notifications={checksheet.notifications}
                  weather={selectedRecord?.weather}
                  createdAt={selectedRecord.createdAt}
                  updatedAt={selectedRecord.updatedAt}
                  facilityState={facilityState}
                  showOutOfServiceComponents
                />
              )}

              <Hide hide={!edit ? 1 : 0}>
                <Inline>
                  <Title>{checksheet.name}</Title>
                  {facilityState && (
                    <Frequency color={facilityState?.color ? `#${facilityState.color}` : undefined}>
                      <Abbr title={facilityState?.name || "DEFAULT"}>
                        {facilityState?.name || "DEFAULT"}
                      </Abbr>
                    </Frequency>
                  )}
                  {submission?.frequency && (
                    <Frequency>{submission.frequency.toUpperCase()}</Frequency>
                  )}
                  {checksheet.draft && <Draft>INCOMPLETE</Draft>}
                </Inline>
                <RenderChecksheet
                  task={withRecordConditionWeather(
                    {...checksheet, builder: state ? state.builder : checksheet.builder},
                    selectedRecord
                  )}
                  taskRecord={selectedRecord}
                  readOnly={false}
                  preview={false}
                  persistToLocalStorage={editing}
                  responses={submission.responses}
                  setSubmission={(responses, outOfRange, outOfRangeMultiple) =>
                    handleEdit(responses, outOfRange, outOfRangeMultiple)
                  }
                  setDraft={
                    selectedRecord.overdue
                      ? (responses, outOfRange, outOfRangeMultiple) =>
                          handleEdit(responses, outOfRange, outOfRangeMultiple, true)
                      : null
                  }
                  cancelFunction={() => setEdit(false)}
                  previousSubmission={selectedRecord.previousSubmission}
                  dateDue={selectedRecord.dateDue}
                  draft={selectedRecord.draft}
                  overdue={selectedRecord.overdue}
                  startDate={selectedRecord.startDate ?? selectedRecord.dateDue}
                  completedAt={selectedRecord.completedAt}
                  allowRemoved
                  completeLoading={completeLoading}
                  draftLoading={draftLoading}
                />
              </Hide>
            </>
          )}

          {exporting && (
            <FormProvider {...form}>
              <Form onSubmit={handleSubmit(createExport)} noValidate>
                <FormGroup>
                  <FormField>
                    <InputText
                      name="exportName"
                      label="Please provide a name for the export."
                      required
                    />
                  </FormField>
                  <FormField>
                    <InputCheck name="help">
                      Include selected help information in export?
                    </InputCheck>
                  </FormField>
                  <FormField>
                    <InputCheckGroup name="fields" label="Target Fields" options={options} all />
                  </FormField>
                </FormGroup>
                <Button type="submit" loading={loadingExport ? 1 : 0}>
                  Export{loadingExport && <ButtonLoader />}
                </Button>
              </Form>
            </FormProvider>
          )}
        </PageLeft>

        {selectedRecord && !detailLoading && submission && (
          <PageRight mode={viewMode}>
            {selectedRecord.weather && (
              <Weather
                ref={weatherRef}
                weather={selectedRecord.weather}
                links={weatherSourceLinks}
              />
            )}

            {selectedRecord?.user !== "GENERATED" && selectedRecord?.completedAt && (
              <RecordResponsibility
                record={selectedRecord}
                versions={updatingVersions ? null : versions}
                currentVersion={currentVersion}
                setCurrentVersion={setCurrentVersion}
              />
            )}

            <RecordHistory
              record={selectedRecord}
              versions={versions}
              currentVersion={currentVersion}
              setCurrentVersion={setCurrentVersion}
              setViewModification={setViewModification}
              viewModification={viewModification}
              edit={edit}
            />

            <RecordNotes
              ref={notesRef}
              checksheet={checksheet}
              record={selectedRecord}
              updateData={updateData}
              state={state}
            />
          </PageRight>
        )}
      </Wrapper>
    </Modal>
  );
};

ModalRecordView.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  checksheet: PropTypes.objectOf(PropTypes.any).isRequired,
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  locks: PropTypes.objectOf(PropTypes.any),
  updateData: PropTypes.func,
  editing: PropTypes.bool,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func
};

// Style Overrides
const HeadingWrapper = styled.div`
  margin-right: ${pad}px;
`;

const Row = styled.section`
  width: 100%;
  padding: 0 0 ${pad}px;
  margin-bottom: ${pad}px;
  ${flex("row", "wrap", "space-between", "end")};
`;

const Wrapper = styled.article`
  ${flex("row", "wrap-reverse", "start", "start")};
`;

const PageLeft = styled.div`
  position: relative;
  width: 100%;
  border: 2px solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad * 1.5}px;

  ${({mode}) =>
    mode === "notes" &&
    css`
      display: none;
    `}

  ${bp(3)} {
    display: block;
    width: 65%;
  }
`;

const PageRight = styled.div`
  width: 100%;

  ${({mode}) =>
    mode === "record" &&
    css`
      display: none;
    `}

  ${bp(3)} {
    position: relative;
    display: block;
    width: 35%;
    padding-left: ${pad}px;

    ${({addingNote}) =>
      addingNote &&
      css`
        position: sticky;
        left: 70%;
        top: 60px;
        z-index: 3;
      `}
  }
`;

const CompletedByWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${pad / 2}px;
`;

const CompletedByEdit = styled.button`
  width: fit-content;

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 0;
  }
`;

const Frequency = styled(Pill)`
  min-width: min-content;
`;
const Draft = styled.span`
  ${voice.quiet}
  text-transform: uppercase;
  border: ${border} solid ${({theme}) => theme.warning};
  padding: ${pad / 10}px ${pad / 4}px;
  border-radius: 10px;
  background: ${({theme}) => theme.warning};
  color: ${({theme}) => theme.tertiary};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-left: ${pad}px;
`;

const Conflict = styled.span`
  width: 100px;
  text-align: center;
  text-transform: uppercase;
  padding: ${pad / 5}px ${pad / 2}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  display: inline-block;
  white-space: nowrap;
  vertical-align: middle;
  margin-right: ${pad}px;
  background-color: ${({theme}) => theme.error};
  ${voice.quiet};
`;

const Info = styled.p`
  padding-bottom: ${pad}px;
  color: ${({theme}) => theme.secondary};

  &:last-child {
    padding: 0;
  }

  @media print {
    display: flex;
    flex-direction: row-reverse;
    align-items: start;
    justify-content: space-between;
    width: 100%;
  }
`;

const ExportInfoPopover = styled.div`
  position: absolute;
  top: 0;
  right: calc(100% + ${pad / 2}px);
  z-index: ${z("above")};
  padding: ${pad}px;
  color: ${({theme}) => theme.tertiary};
  background: ${({theme}) => theme.secondary};
  border-radius: ${radius};
  width: 280px;
  max-width: min(280px, 85vw);
  font-size: 12px;
  line-height: 1.45;
  box-shadow: ${shadow};
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
`;

const ExportButtonAnchor = styled.span`
  position: relative;
  display: inline-flex;

  &:hover ${ExportInfoPopover},
  &:focus-within ${ExportInfoPopover} {
    opacity: 1;
    visibility: visible;
  }
`;

const Export = styled(Button)`
  ${({blocked}) =>
    blocked &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
  ${({loading}) =>
    loading &&
    css`
      opacity: 0.7;
    `}
`;

const ExportInfoTitle = styled.p`
  margin: 0 0 ${pad / 2}px;
  font-weight: 600;
  color: inherit;
`;

const ExportInfoList = styled.ul`
  margin: 0 0 ${pad / 2}px;
  padding-left: ${pad * 1.25}px;
  list-style: disc;

  li {
    margin-bottom: ${pad / 3}px;
    line-height: 1.45;
  }
`;

const ExportInfoDetailLabel = styled.p`
  margin: ${pad * 2}px 0 ${pad / 4}px;
  font-weight: 600;
  color: inherit;
`;

const ExportInfoDetail = styled.p`
  margin: 0;
  color: inherit;
  line-height: 1.45;
`;

const StyledInline = styled(Inline)`
  justify-content: space-between;
  margin-top: ${pad}px;
  width: 100%;

  ${bp(3)} {
    margin-top: 0;
    width: unset;
  }
`;

const SpacedInline = styled(Inline)`
  gap: ${pad}px;
`;

const ModeToggle = styled(Button)`
  ${bp(3)} {
    display: none;
  }
`;

const Status = styled.span`
  ${voice.quiet};
  width: 100px;
  text-align: center;
  text-transform: uppercase;
  padding: ${pad / 5}px ${pad / 2}px;
  border-radius: ${radius};
  color: ${({theme}) => theme.tertiary};
  display: inline-block;
  white-space: nowrap;
  display: block;
  margin-bottom: ${pad}px;

  background: ${({status, theme}) => {
    if (status === "Incomplete") return theme.warning;
    if (status === "Overdue") return theme.error;
    if (status === "Late") return theme.alert;
    return theme.success;
  }};

  ${({archived}) =>
    archived &&
    css`
      opacity: 0.7;
    `}

  svg {
    fill: ${({theme}) => theme.tertiary};
  }

  @media print {
    margin: 0;
  }
`;

const Hide = styled.div`
  ${({hide}) =>
    hide &&
    css`
      display: none;
    `}
`;

const MobileBlock = styled.span`
  ${({overdue, theme}) =>
    overdue &&
    css`
      color: ${theme.error};
    `}
`;

const Filters = styled.div`
  position: relative;
`;

const FilterButton = styled(Button)`
  overflow: initial;
`;

const Edit = styled(Button)`
  transition: opacity 100ms;
  opacity: 1;
  visibility: visible;

  ${({locked}) =>
    locked
      ? css`
          opacity: 0.5;
          cursor: not-allowed;
        `
      : ""}

  ${({loading}) =>
    loading
      ? css`
          opacity: 0;
          visibility: hidden;
        `
      : ""}
`;

export default ModalRecordView;
